import {
  Profile,
  ProfileWithOrganizationProfileInfoAndTeams
} from '@dolfin/business/custom_types'
import { Database, Tables } from '@dolfin/business/db_types'
import { QueryKeys } from '@dolfin/hooks/queryKeys'
import { useOrgContext } from '@dolfin/hooks/state/organization'
import { useSupabaseClient } from '@dolfin/hooks/supabase'
import Image from 'next/image'
import { useQuery } from 'react-query'
import { Sizes, sizes, text } from './constants'

type AvatarProfile = Pick<
  Tables<'profiles'>,
  'id' | 'name' | 'email' | 'img_url'
>

interface AvatarProps {
  size?: Sizes
  className?: string
  userId?: string
  withDetails?: boolean
  withName?: boolean
  profileData?:
    | Profile
    | ProfileWithOrganizationProfileInfoAndTeams
    | AvatarProfile
}

const Avatar = ({
  userId,
  profileData,
  withDetails,
  withName,
  size = Sizes.MEDIUM,
  className
}: AvatarProps) => {
  const supabase = useSupabaseClient<Database>()

  const { selectedProfile, selectedOrganizationId: _selectedOrganizationId } =
    useOrgContext(state => ({
      selectedProfile: state.profile,
      selectedOrganizationId: state.id
    }))

  const { isLoading, data: profiles } = useQuery<AvatarProfile | null>(
    [QueryKeys.profiles.get, userId],
    async () => {
      if (!userId) return null

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .limit(1)
        .single()

      if (error) {
        console.error('Error fetching profile:', error)
      }
      return data || null
    },
    {
      enabled: !profileData && !!userId
    }
  )

  const profile: AvatarProfile | null =
    (profileData
      ? (profileData as AvatarProfile)
      : userId
        ? profiles
        : (selectedProfile as AvatarProfile | null)) ?? null

  return (
    <div className={'flex space-x-2 items-center'}>
      <div
        className={`relative bg-primary-negative border-2 rounded-full flex items-center justify-center shrink-0 transition-all duration-200 ${
          sizes[size]
        } ${isLoading ? 'animate-pulse' : ''} ${className ?? ''} overflow-hidden`}>
        {!isLoading &&
          profile &&
          (profile.img_url ? (
            <Image
              fill
              style={{ objectFit: 'cover' }}
              src={profile.img_url}
              alt={profile.name ?? profile.email}
            />
          ) : (
            <p className={`text-neutral-900 font-bold ${text[size]}`}>
              {(profile.name ?? profile.email)[0].toUpperCase()}
            </p>
          ))}
      </div>

      {(withDetails || withName) && (
        <div className={'flex flex-col items-start justify-center w-[70%]'}>
          {!!profile?.name && (
            <p className={'text-left font-semibold w-full truncate'}>
              {profile.name.slice(0, 1).toUpperCase() + profile.name.slice(1)}
            </p>
          )}
          {withDetails && !!profile?.email && (
            <p className={'text-xs text-left w-full truncate'}>
              {profile.email}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default Avatar
